/* ==========================================================================
   Young Grandpa - product card gallery drag-vs-click fix
   --------------------------------------------------------------------------
   Bug: dragging the image carousel on a product card (snippets/card-gallery.
   liquid wraps <slideshow-component> in <a ref="cardGalleryLink">) correctly
   changes the image, but also navigates to the product page - the drag is
   being read as a click.

   Root cause, in stock assets/slideshow.js (not edited here - Slideshow is
   reused everywhere: PDP galleries, marquees, every product card - a fix
   there is a sitewide blast radius for what's actually a narrow bug):
   Slideshow's own #handleMouseDown already tries to solve this. Once real
   movement is detected it arms `document.addEventListener('click',
   preventDefault, { once: true, signal })` specifically to swallow the
   click that a drag-release produces. But its onPointerUp calls
   `controller.abort()` as its very first line - which tears down that same
   listener (it shares the signal) immediately, synchronously, before the
   browser has dispatched the click. click always fires after pointerup/
   mouseup in the same synchronous input sequence, so by the time it
   arrives the listener meant to catch it is already gone. The safeguard
   Slideshow clearly intends to have never actually runs.
   <product-card>'s own delegated click handler (assets/product-card.js
   navigateToProduct) doesn't help either: it deliberately no-ops when the
   click landed on a real <a> (cardGalleryLink here) and defers to native
   anchor navigation - which is exactly what then fires, unblocked.

   Fix: an independent drag-vs-click detector, scoped to <slideshow-
   component>, that doesn't depend on Slideshow's own (broken) bookkeeping.
   Tracks real pointer movement per-pointerId; once it clears a small
   threshold, tags that specific <slideshow-component> as "just dragged" and
   a capture-phase click listener on document - which runs before anything
   else, including the anchor's own default navigation and product-card's
   bubble-phase handler - swallows the next click on it and clears the tag.
   A plain tap/click (no movement past the threshold) is left completely
   alone. Works identically for mouse and touch: both produce PointerEvents
   with real clientX/clientY, and both produce a click after release.
   ========================================================================== */

(function () {
  'use strict';

  var GALLERY = 'slideshow-component';
  var THRESHOLD = 10;
  /* Safety net only: clears a stale "just dragged" tag if, for whatever
     reason, no click ever follows the drag release. */
  var STALE_TAG_MS = 500;

  /** pointerId -> { startX, startY, root: Element, dragged: boolean } */
  var gestures = new Map();

  /**
   * @param {PointerEvent} event
   */
  function onPointerDown(event) {
    var root = event.target instanceof Element ? event.target.closest(GALLERY) : null;
    if (!root) return;

    gestures.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      root: root,
      dragged: false,
    });
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerMove(event) {
    var gesture = gestures.get(event.pointerId);
    if (!gesture || gesture.dragged) return;

    var dx = event.clientX - gesture.startX;
    var dy = event.clientY - gesture.startY;
    if (Math.abs(dx) <= THRESHOLD && Math.abs(dy) <= THRESHOLD) return;

    gesture.dragged = true;
    gesture.root.ygSuppressClick = true;
    clearTimeout(gesture.root.ygSuppressClickTimer);
    gesture.root.ygSuppressClickTimer = setTimeout(function () {
      gesture.root.ygSuppressClick = false;
    }, STALE_TAG_MS);
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerEnd(event) {
    gestures.delete(event.pointerId);
  }

  /**
   * @param {MouseEvent} event
   */
  function onClickCapture(event) {
    var root = event.target instanceof Element ? event.target.closest(GALLERY) : null;
    if (!root || !root.ygSuppressClick) return;

    root.ygSuppressClick = false;
    clearTimeout(root.ygSuppressClickTimer);
    event.preventDefault();
    event.stopPropagation();
  }

  /* Capture phase throughout: this must see pointermove before Slideshow's
     own bubble-phase document listener can call stopImmediatePropagation on
     it (assets/slideshow.js), and the click listener must run before the
     anchor's default navigation and product-card's own bubble-phase
     handler (assets/product-card.js navigateToProduct) get a chance to act. */
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerEnd, true);
  document.addEventListener('pointercancel', onPointerEnd, true);
  document.addEventListener('click', onClickCapture, true);
})();
